import { CommanderAPI } from '../../../../../redux/commander/api/CommanderAPI';
import {
  useAppDispatch,
  useAppSelector,
} from '../../../../../redux/store.model';
import { getMachinesTypeSelector } from '../../../../../redux/composableDevice/selectors/composableDeviceSelectors';
import ControlUnitAPI from '../../../../../redux/controlUnit/api/ControlUnitAPI';
import { ControlUnitAPIResponse } from '../../../../../redux/controlUnit/api/ControlUnit.model';
import {
  getControlUnitsActiveByIdFilter,
  getControlUnitsVisibleByIdFilter,
} from '../../../../../redux/controlUnit/selectors';
import { postGroup } from '../../../../../redux/groups/actions/thunks';
import { Base64 } from '../../../../../util/Base64';
import { ComposableDeviceUserInterfaces } from '../../../../../redux/composableDevice/ComposableDevice.model';
import useAllMerchandisers from './useAllMerchandisers';
import { getChannelsByComposableDevice } from '../../../util/getChannelsByComposableDevice';
import { putDevice } from '../../../../../redux/commander/actions/thunks';
import { getMyWorkspaceSelector } from '../../../../../redux/stoerkID/selectors/StoerkId.selectors';

interface CreateMerchandiser {
  merchandiserType: string;
  commanderSerialNumber: string;
  merchandiserName: string;
  location?: {
    street: string;
    zipCode: string;
    city: string;
    country: string;
  };
  timezone: string;
}
export const useCreateMerchandiser = (
  groupId: string,
  workspaceId?: string
) => {
  const { merchandisers } = useAllMerchandisers({
    groupId,
    workspaceId,
  });
  const devicesInStore = merchandisers?.flatMap((m) => m.devices) ?? [];

  const [getCommanderBySerialNumber] =
    CommanderAPI.useLazyGetCommanderBySerialNumberQuery();
  const [updateCommanderTimezone] =
    CommanderAPI.useSetTimezoneCommanderMutation();
  const machineTypes = useAppSelector(getMachinesTypeSelector);
  const [getControlUnitsBySerialNumber] =
    ControlUnitAPI.useLazyGetControlUnitsBySerialNumberQuery();

  const dispatch = useAppDispatch();
  let errorMessage = '';

  const createMerchandiser = async (params: CreateMerchandiser) => {
    const { merchandiserType, commanderSerialNumber, merchandiserName } =
      params;
    try {
      const machineType = machineTypes.find(
        (type) => type.id === merchandiserType
      );
      if (
        machineType?.userInterface !==
        ComposableDeviceUserInterfaces.SCC_MERCHANDISE_V2
      ) {
        errorMessage = 'freshwatch.merchandiser.add.machine_type_not_valid';
        throw new Error(errorMessage);
      }
      const commanderInfo = await getCommanderBySerialNumber(
        commanderSerialNumber.trim()
      ).unwrap();
      if (!commanderInfo) {
        errorMessage = 'freshwatch.merchandiser.add.device_not_found';
      } else if (devicesInStore.includes(Base64.decode(commanderInfo?.uuid))) {
        errorMessage = 'freshwatch.merchandiser.add.device_already_exists';
      }
      if (errorMessage) {
        throw new Error(errorMessage);
      }

      const commanderId = Base64.decode(commanderInfo.uuid);

      const controlUnits = await getControlUnitsBySerialNumber(
        commanderSerialNumber
      );
      const validControlUnits = controlUnits?.data?.filter(
        (cu: ControlUnitAPIResponse) =>
          getControlUnitsActiveByIdFilter(cu) &&
          getControlUnitsVisibleByIdFilter(cu)
      );

      const default_channel =
        machineType?.labels['merchandiser_default_channel'] || '';
      const default_image = machineType?.labels['default_image'] || '';

      const channelList = getChannelsByComposableDevice(
        machineType,
        validControlUnits as ControlUnitAPIResponse[],
        commanderId
      );

      const group = {
        name: merchandiserName,
        parentGroupID: groupId ?? '',
        composableDevice: {
          machineTypeId: machineType?.id || '',
          channels: channelList,
          labels: {
            default_channel: default_channel,
            serial_number: commanderSerialNumber,
            imageURL: default_image,
          },
        },
        imageURL: '',
        logoURL: default_image,
        devices: [commanderId],
      };
      await dispatch(postGroup(group, undefined, groupId ?? ''));

      await dispatch(
        putDevice(commanderId, {
          ...commanderInfo,
          name: merchandiserName,
          street: params.location?.street || '',
          zipcode: params.location?.zipCode || '',
          city: params.location?.city || '',
          country: params.location?.country || '',
        })
      );

      await updateCommanderTimezone({
        commanderId,
        timezone: params.timezone,
      });
    } catch (error) {
      if (typeof error === 'string') {
        errorMessage = error;
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }
      throw new Error(errorMessage);
    }
  };
  return [createMerchandiser] as const;
};
